// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/questions.ts
// Ported verbatim: ids, options, `when`, `ack`, `planFor`, `answerText`. Not ported: the channel logos. TP fetched each
// channel's favicon by domain; here the channel options use generic lucide icons (no brand logo, no network, no domain).
// Ours: `channelName` guards against inherited keys (Object.hasOwn).
/**
 * The profile questionnaire. It only asks what the research can't find on its
 * own: revenue and spend (never public), how spend splits across channels
 * (only Meta's ad volume is measurable from outside), what's holding growth
 * back, where the brand sells more per order and what brings buyers back.
 * Everything else (category, prices, creative mix, competitors) comes from
 * the site and the ad libraries while these questions are open.
 *
 * Money is asked in ranges with a "Prefer not to say": a bracket gets an
 * answer where an exact number gets skipped. Pains are ranked, not just
 * picked, so the top one is known. The main channel is its own single-choice
 * question, with the other channels optional right after it.
 *
 * Order: channels first (visual, one tap, and it steers the research at
 * once); then what holds growth back; where they sell more per order and what
 * brings customers back (AOV and LTV, asked as places and habits, not
 * metrics); money last (spend, revenue, AOV), with "Prefer not to say" making
 * the opt-out free. The KPI is asked later, when the first plan is built.
 *
 * The counter is dynamic: a question with `when` joins the plan only once the
 * answers so far make it relevant (no spend questions for a brand that isn't
 * running ads yet), so "/6" can become "/10" mid-way.
 *
 * `profile-input.ts` turns the answers into the typed brand-profile input.
 */
import type { ComponentType } from 'react'
import {
  Banknote,
  Box,
  Building2,
  ChartNoAxesColumn,
  Check,
  CircleDashed,
  CircleHelp,
  Crown,
  EyeOff,
  Gauge,
  Ghost,
  Handshake,
  Landmark,
  Layers,
  Lightbulb,
  Megaphone,
  MousePointerClick,
  Music,
  Pencil,
  PieChart,
  Pin,
  Play,
  Radar,
  Repeat,
  Rocket,
  Search,
  Shuffle,
  ShoppingCart,
  Sprout,
  Swords,
  Ticket,
  TrendingUp,
  User,
  Users,
  Wallet,
  X,
} from 'lucide-react'

export type QuestionOption = {
  id: string
  label: string
  /** First-person pain line under the label. */
  hint?: string
  icon: ComponentType<{ className?: string }>
  /** Escape hatch: the row is a free-text input. */
  freeText?: boolean
  /** Picking it clears every other choice (multi-select only). */
  exclusive?: boolean
}

export type Question = {
  id: string
  /** A function when the wording depends on earlier answers (see `questionTitle`). */
  title: string | ((answers: Answers) => string)
  subtitle?: string
  /** `rank`: the answer is an order of importance (`selected` is most important first). */
  mode: 'single' | 'multi' | 'rank'
  /** What the answer is about, in two or three words ("ad spend"), for "you skipped …". */
  topic: string
  options: QuestionOption[]
  /**
   * A row shown above the options, fixed at #1 and not part of the answer: the main
   * channel on "Any other channels?", so the ranking reads in full (set by `visibleOptions`).
   */
  pinned?: QuestionOption
  /** Only part of the plan when this returns true for the answers so far. */
  when?: (answers: Answers) => boolean
  /**
   * The agent's one-line reply once this is answered: what the answer changes,
   * never a claim of work that isn't happening. Nothing for a skip.
   */
  ack?: (answer: Answer, answers: Answers) => string | null
}

export type Answer = { selected: string[]; text?: string; skipped?: boolean }
export type Answers = Record<string, Answer | undefined>

/** Paid channels, in the order they are offered. */
export const CHANNEL_LABELS: Record<string, string> = {
  meta: 'Meta',
  google: 'Google',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  pinterest: 'Pinterest',
  snapchat: 'Snapchat',
  amazon: 'Amazon Ads',
  influencers: 'Influencers / affiliates',
}

/** A channel's display name; custom channels are stored as their own label. */
export const channelName = (id: string) => (Object.hasOwn(CHANNEL_LABELS, id) ? CHANNEL_LABELS[id]! : id)

/** The paid channels, main first: the one answer to `channels`, then `otherChannels` ("none" excluded). */
export function paidChannels(answers: Answers): string[] {
  const picked = (a: Answer | undefined) =>
    !a || a.skipped ? [] : [...a.selected, ...(a.text ? [a.text] : [])].filter((c) => c !== 'none')
  const main = picked(answers.channels).slice(0, 1)
  return [...main, ...picked(answers.otherChannels).filter((c) => !main.includes(c))]
}

const runsAds = (a: Answers) => paidChannels(a).length > 0

const CHANNEL_OPTIONS: QuestionOption[] = [
  { id: 'meta', label: 'Meta (Facebook / Instagram)', icon: Megaphone },
  { id: 'google', label: 'Google (Search / Shopping / PMax)', icon: Search },
  { id: 'youtube', label: 'YouTube', icon: Play },
  { id: 'tiktok', label: 'TikTok', icon: Music },
  { id: 'pinterest', label: 'Pinterest', icon: Pin },
  { id: 'snapchat', label: 'Snapchat', icon: Ghost },
  { id: 'amazon', label: 'Amazon Ads', icon: ShoppingCart },
  { id: 'influencers', label: 'Influencers / affiliates', icon: Handshake },
]

export const QUESTIONS: Question[] = [
  {
    id: 'channels',
    topic: 'paid channels',
    // One answer, so the main channel is never a guess from tap order (select-then-rank
    // has no precedent in ad tools); the rest come in the next, optional question.
    title: 'Where does most of your paid traffic come from?',
    mode: 'single',
    options: [
      ...CHANNEL_OPTIONS,
      { id: 'none', label: 'Not running paid ads yet', icon: CircleDashed },
      { id: 'own', label: 'Somewhere else…', icon: Pencil, freeText: true },
    ],
    ack: (_a, answers) => {
      const [main] = paidChannels(answers)
      return main
        ? `Got it: ${channelName(main)} first. I'll start the ad research there.`
        : "Got it, no paid ads yet. I'll look at what brands like yours run to get started."
    },
  },
  {
    id: 'otherChannels',
    topic: 'other channels',
    title: 'Any other channels?',
    subtitle: 'Optional. Tap them in order of spend.',
    // Ranked: the main channel is pinned as #1 and is not offered again (see `visibleOptions`),
    // so the others count from 2 and the research is weighted in that order.
    mode: 'rank',
    when: runsAds,
    options: [...CHANNEL_OPTIONS, { id: 'own', label: 'Add your own answer…', icon: Pencil, freeText: true }],
    ack: (_a, answers) => {
      const [main, ...rest] = paidChannels(answers)
      return main && rest.length ? `Then ${rest.map(channelName).join(', ')}. I'll weight the research in that order.` : null
    },
  },
  {
    id: 'mainShare',
    topic: 'channel split',
    title: (answers) => `How much of your ad spend goes to ${channelName(paidChannels(answers)[0] ?? 'your main channel')}?`,
    mode: 'single',
    // Only a question when there's more than one channel to split between.
    when: (a) => paidChannels(a).length > 1,
    options: [
      { id: 'most', label: 'Almost all of it (80%+)', icon: PieChart },
      { id: 'majority', label: 'Most of it (50–80%)', icon: PieChart },
      { id: 'minority', label: 'Less than half', icon: Shuffle },
    ],
    ack: (a, answers) => {
      const main = channelName(paidChannels(answers)[0] ?? 'your main channel')
      return a.selected[0] === 'minority'
        ? `Got it: ${main} leads, but most of the spend is spread out. I'll research every channel you named.`
        : `Got it: ${main} carries most of the spend, so it gets most of the research.`
    },
  },
  {
    id: 'team',
    topic: 'who runs your ads',
    title: 'Who runs your ads day to day?',
    mode: 'single',
    when: runsAds,
    options: [
      { id: 'founder', label: 'Me (founder / owner)', icon: Crown },
      { id: 'in-house', label: 'In-house team', icon: Building2 },
      { id: 'agency', label: 'Agency', icon: Users },
      { id: 'freelancer', label: 'Freelancer', icon: User },
    ],
    ack: (a) =>
      ({
        founder: "Got it. I'll keep the plan to what one person can run.",
        'in-house': "Got it. I'll write the plan for your team to run.",
        agency: "Got it. I'll make the plan easy to hand to your agency.",
        freelancer: "Got it. I'll keep each test small enough to brief a freelancer.",
      })[a.selected[0] ?? ''] ?? null,
  },
  {
    id: 'pains',
    topic: 'growth blockers',
    title: "What's holding growth back?",
    subtitle: 'Tap the biggest one first.',
    // Ranked: the top pain leads the mission, the research and the first plan.
    mode: 'rank',
    options: [
      { id: 'costs', label: 'Costs keep rising', hint: 'CAC and CPMs are eating the margin', icon: TrendingUp },
      { id: 'scale', label: "Can't scale without losing efficiency", hint: 'More budget, worse returns', icon: Gauge },
      { id: 'fatigue', label: 'Ads fatigue fast', hint: 'We need new winning concepts', icon: Lightbulb },
      { id: 'volume', label: "Can't produce enough creative", hint: "We can't keep up at our scale", icon: Layers },
      { id: 'competitors', label: 'Competitors are taking share', hint: "They're outspending or out-creating us", icon: Swords },
      { id: 'conversion', label: "Site / landing pages don't convert", hint: "Clicks come, orders don't", icon: MousePointerClick },
      { id: 'tracking', label: "Can't trust our tracking", hint: "We don't know what's working", icon: Radar },
      { id: 'starting', label: 'Just starting', hint: "Don't know where to begin", icon: Rocket },
      { id: 'else', label: 'Something else…', icon: Pencil, freeText: true },
    ],
    ack: (a, answers) => {
      const top = a.selected[0]
      if (!top) return null
      if (top === 'competitors') return "Noted. I'll put the competitor ad research first."
      if (top === 'fatigue' || top === 'volume') return "Noted. I'll look for the concepts that last longest in your category."
      if (top === 'conversion') return "Noted. I'll compare your landing pages with your competitors'."
      return runsAds(answers) ? "Noted. That's where the first plan will start." : "Noted. I'll start the plan there."
    },
  },
  {
    id: 'upsell',
    topic: 'one-click upsell',
    // Asked straight: a yes/no is what the plan needs, and "No" is the funnel
    // step to propose.
    title: 'Do you run a one-click upsell after checkout?',
    subtitle: 'An extra offer right after payment, added without re-entering the card.',
    mode: 'single',
    options: [
      { id: 'yes', label: 'Yes', icon: Check },
      { id: 'no', label: 'No', icon: X },
      { id: 'unsure', label: 'Not sure', icon: CircleDashed },
    ],
    ack: (a) =>
      ({
        yes: "Noted. I'll check how your upsell compares with your competitors'.",
        no: "Noted. An upsell after payment is a funnel step I'll look at.",
      })[a.selected[0] ?? ''] ?? null,
  },
  {
    id: 'subscription',
    topic: 'subscriptions',
    title: 'Do you sell subscriptions?',
    subtitle: 'Subscribe & save, autoship, boxes or a paid membership.',
    mode: 'single',
    options: [
      { id: 'yes', label: 'Yes', icon: Check },
      { id: 'no', label: 'No', icon: X },
      { id: 'oneTime', label: 'No, not a repeat purchase', hint: 'e.g. mattresses, furniture', icon: Box },
    ],
    ack: (a) =>
      ({
        no: "Noted. A subscription is a lever I'll weigh in the plan.",
        oneTime: "Got it. Then the first order has to pay for itself, and I'll plan around that.",
      })[a.selected[0] ?? ''] ?? null,
  },
  {
    id: 'subscriptionType',
    topic: 'subscription type',
    title: 'What kind of subscription?',
    mode: 'single',
    when: (a) => a.subscription?.selected[0] === 'yes',
    options: [
      { id: 'replenishment', label: 'Replenishment / autoship', hint: 'The same product on a schedule', icon: Repeat },
      { id: 'box', label: 'Curated box', hint: 'A new selection every period', icon: Box },
      { id: 'service', label: 'Membership or service', hint: 'Access or perks, not a product', icon: Ticket },
    ],
    ack: () => "Got it. I'll compare your subscription with brands running the same model.",
  },
  {
    id: 'spend',
    topic: 'ad spend',
    title: 'What did you spend on paid ads in the last 30 days?',
    subtitle: 'All channels together. A range is fine.',
    mode: 'single',
    when: runsAds,
    options: [
      // Four wide buckets (Arcads-style cuts): enough to place the brand in a
      // spend league, without a long list to read.
      { id: 'lt10k', label: 'Under $10K', icon: Sprout },
      { id: '10-50k', label: '$10K–$50K', icon: Wallet },
      { id: '50-250k', label: '$50K–$250K', icon: Landmark },
      { id: '250k+', label: '$250K+', icon: TrendingUp },
      { id: 'undisclosed', label: 'Prefer not to say', icon: EyeOff },
    ],
    ack: (a) =>
      a.selected[0] === 'undisclosed'
        ? "No problem. I'll compare you with brands your size instead."
        : "Thanks. I'll benchmark you against brands spending in the same range.",
  },
  {
    id: 'revenue',
    topic: 'revenue',
    title: "Roughly what's your annual online revenue?",
    subtitle: 'A range is fine. It sets the benchmarks we compare you with.',
    mode: 'single',
    options: [
      { id: 'lt250k', label: 'Under $250K', icon: Sprout },
      { id: '250k-1m', label: '$250K–$1M', icon: Wallet },
      { id: '1-5m', label: '$1M–$5M', icon: Banknote },
      { id: '5-15m', label: '$5M–$15M', icon: Landmark },
      { id: '15-50m', label: '$15M–$50M', icon: ChartNoAxesColumn },
      { id: '50m+', label: '$50M+', icon: TrendingUp },
      { id: 'undisclosed', label: 'Prefer not to say', icon: EyeOff },
    ],
    ack: (a) =>
      a.selected[0] === 'undisclosed'
        ? "No problem. I'll compare you with brands in your category instead."
        : 'Thanks. That sets which brands I compare you with.',
  },
  {
    id: 'aov',
    topic: 'order value',
    title: "What's your average order value?",
    subtitle: 'A range is fine.',
    mode: 'single',
    options: [
      { id: 'lt50', label: 'Under $50', icon: Sprout },
      { id: '50-100', label: '$50–$100', icon: Wallet },
      { id: '100-200', label: '$100–$200', icon: Banknote },
      { id: '200-500', label: '$200–$500', icon: Landmark },
      { id: '500+', label: '$500+', icon: TrendingUp },
      { id: 'unsure', label: 'Not sure', icon: CircleHelp },
    ],
    ack: (a) =>
      a.selected[0] === 'unsure'
        ? "No problem. I'll estimate it from your product prices."
        : 'Got it. That sets which offers are worth testing.',
  },
]

/**
 * The answer as the person would say it, for the conversation: the labels they
 * picked (numbered for a ranking), their own words for a typed answer, or
 * "Skipped".
 */
export function answerText(question: Question, answer: Answer): string {
  if (answer.skipped) return 'Skipped'
  const label = (id: string) => question.options.find((o) => o.id === id)?.label ?? channelName(id)
  const picked = [...answer.selected.map(label), ...(answer.text ? [answer.text] : [])]
  // The other channels count from 2: the main one is #1.
  const first = question.id === 'otherChannels' ? 2 : 1
  if (question.mode === 'rank') return picked.map((p, i) => `${i + first}. ${p}`).join(' · ')
  return picked.join(', ')
}

/**
 * The agent's reply to an answer, only when it adds something: the question's own
 * line about what the answer changes. Null for a skip or a question with nothing to
 * say, so the chat doesn't fill with "Noted.".
 */
export function replyTo(question: Question, answer: Answer, answers: Answers): string | null {
  if (answer.skipped) return null
  return question.ack?.(answer, answers) ?? null
}

/** The questions currently in the plan, given the answers so far. */
export function planFor(answers: Answers): Question[] {
  return QUESTIONS.filter((q) => !q.when || q.when(answers))
}

/** A question's wording for the answers so far. */
export function questionTitle(question: Question, answers: Answers): string {
  return typeof question.title === 'function' ? question.title(answers) : question.title
}

/**
 * A question as shown for the answers so far: "Any other channels?" leaves out the main
 * one and pins it above the options as #1.
 */
export function visibleOptions(question: Question, answers: Answers): Question {
  if (question.id !== 'otherChannels') return question
  const [main] = paidChannels(answers)
  if (!main) return question
  const known = question.options.find((o) => o.id === main)
  return {
    ...question,
    options: question.options.filter((o) => o.id !== main),
    pinned: { id: main, label: known?.label ?? main, icon: known?.icon ?? Pencil, hint: 'Main channel' },
  }
}

/** The topics of the questions in the plan that were skipped, in plan order. */
export function skippedTopics(answers: Answers): string[] {
  return planFor(answers)
    .filter((q) => answers[q.id]?.skipped)
    .map((q) => q.topic)
}
